import React, { useState, useEffect, useRef } from 'react';
import myheaders from './myHeaders.css'
import { useSelector, useDispatch } from "react-redux";
import { useParams, useSearchParams } from 'react-router-dom';
import { Input, Button, message, } from 'antd';
import { PlusOutlined, EditOutlined, ArrowLeftOutlined, EyeOutlined, LoadingOutlined } from '@ant-design/icons';
import { debounce } from "lodash";
import { padServices } from '../../api';
import { ReactComponent as Ai } from '../../assets/ai_post.svg';
import { generateAiText } from '../AiTextButton/generateAiText';
import { setEditorSummary, updateIsSaving } from '../../app/EditorSlice';
import AiTextButton from '../AiTextButton/AiTextButton';
import { emitLoadData } from '../../Socket';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { saveToLocalStorage } from '../../utils/helper';
const { TextArea } = Input;

const Headers = ({ isMobile, isOpenOutline: isOpenOutlineProp, setIsOpenOutline: setIsOpenOutlineProp }) => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const [isEditMode, setIsEditMode] = useState(false);
  const [isOpenOutlineInternal, setIsOpenOutlineInternal] = useState(true);
  const isOpenOutline = typeof isOpenOutlineProp !== 'undefined' ? isOpenOutlineProp : isOpenOutlineInternal;
  const setIsOpenOutline = setIsOpenOutlineProp || setIsOpenOutlineInternal;
  const [searchParams] = useSearchParams();
  const documentName = searchParams.get('name');
  const editorContent = useSelector((state) => state.editor.editorInstancesDelta); // Change the selector to match the Draft.js editor content
  const isReadOnly = useSelector((state) => state.editor.isReadOnly);
  const [headings, setHeadings] = useState([]);
  const summaryRef = useRef();
  const [aiModalVisibility, setAiModalVisibility] = useState(false);
  const [isOpenAi, setIsOpenAi] = useState(false);
  const documentVersionData = useSelector((state) => state.editor.documentVersionData);
  const summaryText = useSelector((state) => state.editor.editorSummary);
  const isChangeByUser = useSelector((state) => state.editor.isChangeByUser);
  const signatures = useSelector((state) => state.editor.signatures);
  const editor = useSelector((state) => state.editor.lexicalEditor);
  const isTemplateDocument = useSelector((state) => state.editor.isTemplateDocument);

  useEffect(() => {
    if (documentVersionData.isVersionChange) {
      loadDocumentVersionSummary(documentVersionData.versionData);
    }
  }, [documentVersionData]);
  const loadDocumentVersionSummary = (versionData) => {
    dispatch(setEditorSummary(versionData?.summary || ''));
  }

  useEffect(() => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(editorContent, 'text/html');
    const headingElements = doc.querySelectorAll('h1, h2, h3, h4, h5, h6');
    const extractedHeadings = Array.from(headingElements).map((element) => ({
      level: parseInt(element.tagName[1], 10),
      text: element.textContent.trim(),
      id: element.getAttribute('id'),
    }));
    setHeadings(extractedHeadings);
  }, [editorContent]);

  const debouncedSaveToDatabase = useRef(debounce((summary) => {
    const isOffline = !window.navigator.onLine;
    if (isOffline) {
      const currentEditorStateJSON = editor.getEditorState().toJSON();
      // Save to localStorage if offline
      saveToLocalStorage(id, {
        milepadData: currentEditorStateJSON,
        summary: summary,
        Signatures: signatures,
      });
      return;
    }
    else {
      padServices.saveToDatabase(id, null, summary, dispatch);
      if (isChangeByUser) {
        emitLoadData({
          data: summary,
          type: 'summary',
          documentId: id,
        });
      }
    }
  }, 800)).current;

  const handlePlusClick = () => {
    setIsEditMode(true);
    summaryRef && summaryRef.current && summaryRef.current.focus();
  };

  const handleEditClick = () => {
    setIsEditMode(true);
  };

  const handleTextareaChange = (e) => {
    dispatch(updateIsSaving({ isSaving: 'Saving...', lastSavedDateTime: '' }));
    debouncedSaveToDatabase(e.target.value);
    dispatch(setEditorSummary(e.target.value));
  };

  const handleBlur = () => {
    setIsEditMode(false);
  };

  const handleCloseClick = () => {
    setIsOpenOutline(false);
  };

  const handleOpenClick = () => {
    setIsOpenOutline(true);
  };

  useEffect(() => {
    // Update CSS var and add a class to the root for transitions when outline toggles
    document.documentElement.style.setProperty('--outline-width', isOpenOutline ? '300px' : '0px');
    if (!isOpenOutline) {
      document.documentElement.classList.add('outline-collapsed');
    } else {
      document.documentElement.classList.remove('outline-collapsed');
    }
  }, [isOpenOutline]);

  const showAiText = (data) => {
    if (data) {
      dispatch(setEditorSummary(data));
      padServices.saveToDatabase(id, null, data, dispatch);
      if (isChangeByUser) {
        emitLoadData({
          data: data,
          type: 'summary',
          documentId: id,
        });
      }
    }
  }
  const renderSummaryText = () => {
    return (
      <div className='summary-content-container'>
        <div className='summary-header'>
          <h3>Summary</h3>
          <div className='summary-actions'>
            <Button
              type="text"
              title="Generate AI Summary"
              icon={<Ai className='ai-icon-button' />}
              onClick={() => setAiModalVisibility(true)}
              disabled={isReadOnly}
            />
            {!summaryText ? (
              <Button
                title="Add summary"
                type="text"
                icon={<PlusOutlined />}
                onClick={handlePlusClick}
                disabled={isReadOnly}
              />
            ) : (
              !isEditMode && summaryText && (
                <Button
                  type="text"
                  title="Edit summary"
                  icon={<EditOutlined />}
                  onClick={handleEditClick}
                  disabled={isReadOnly}
                />
              )
            )}
          </div>
        </div>

        {isEditMode ? (
          <TextArea
            className={'textarea-editable'}
            autoSize={{ minRows: 2 }}
            value={summaryText}
            onChange={handleTextareaChange}
            onBlur={handleBlur}
            ref={summaryRef}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                setIsEditMode(false);
              }
            }}
          />
        ) : (
          summaryText && <TextArea
            className='textarea-readonly'
            autoSize={{ minRows: 2 }}
            value={summaryText}
            readOnly
          />
        )}
      </div>
    );
  };


  return (
    <>
      {isOpenOutline ? (
        <div className="main-content-container">
          <Button
            title="Close document outline"
            type="text"
            icon={<ArrowLeftOutlined />}
            onClick={handleCloseClick}
          />
          <div className="content-wrapper">
            {!isTemplateDocument && <div className="summary-section">{renderSummaryText()}</div>}
            <h1 className="document-name">{documentName}</h1>
            {!isTemplateDocument && <hr className='divider-conatiner' />}
            <div className="headers-container">
              <h3 className="outline-title">Outline</h3>
              <div className="headings-container">
                {headings.length > 0 ? (
                  headings.map((heading, index) =>
                    heading.text.trim() !== '' && (
                      <div
                        key={index}
                        className="heading-item"
                        style={{
                          marginLeft: `${(heading.level - 1) * 10}px`,
                        }}
                      >
                        <span
                          style={{
                            fontWeight: heading.level === 1 ? '600' : 'normal',
                          }}
                        >
                          {heading.text}
                        </span>
                      </div>
                    )
                  )
                ) : (
                  <h1 className="no-headings-message">
                    Headings that you add to the document will appear here.
                  </h1>
                )}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <EyeOutlined
          title="Show document outline"
          onClick={handleOpenClick}
          width={20}
        />
      )}
      <div>
        <AiTextButton
          summaryContent={editorContent}
          isOpen={isOpenAi}
          setIsOpenAi={setIsOpenAi}
          showAiText={showAiText}
          setAiModalVisibility={setAiModalVisibility}
          aiModalVisibility={aiModalVisibility}
        />
      </div>
    </>
  );
};

export default Headers;